import * as THREE from 'three';
import { COURT_W, COURT_H, HALF_W, HALF_H, GOAL_W, GOAL_LINE_Y, CORNER_R, CREASE_R } from './constants';

// ─── Helpers ──────────────────────────────────────────────────────────────────
function makeCanvas(w, h) {
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    return [c, c.getContext('2d')];
}

function toTexture(canvas, { srgb = true, aniso = 1, repeat = null } = {}) {
    const tex = new THREE.CanvasTexture(canvas);
    if (srgb) tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = aniso;
    if (repeat) {
        tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
        tex.repeat.set(repeat[0], repeat[1]);
    }
    tex.needsUpdate = true;
    return tex;
}

// Seeded PRNG so procedural textures look identical on every load
export function rng(seed = 1) {
    let s = seed >>> 0;
    return () => {
        s = (s + 0x6D2B79F5) >>> 0;
        let t = s;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

export function luminance(color) {
    const c = new THREE.Color(color);
    return 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
}

function roundRectPath(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

// ─── Sport-court floor (interlocking tiles + painted markings) ────────────────
export function makeFloorTexture(aniso) {
    const PPU = 64;                       // pixels per world unit
    const W = COURT_W * PPU, H = COURT_H * PPU;
    const [c, ctx] = makeCanvas(W, H);
    const X = x => (x + HALF_W) * PPU;    // world → canvas
    const Y = y => (HALF_H - y) * PPU;
    const rand = rng(7);

    // Base tiles
    const tile = 0.3 * PPU;
    for (let ty = 0; ty < H; ty += tile) {
        for (let tx = 0; tx < W; tx += tile) {
            const v = 0.93 + rand() * 0.1;
            ctx.fillStyle = `rgb(${Math.round(24 * v)},${Math.round(74 * v)},${Math.round(128 * v)})`;
            ctx.fillRect(tx, ty, tile, tile);
        }
    }
    // Tile seams
    ctx.strokeStyle = 'rgba(0,0,0,0.22)';
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    for (let tx = 0; tx <= W; tx += tile) { ctx.moveTo(tx, 0); ctx.lineTo(tx, H); }
    for (let ty = 0; ty <= H; ty += tile) { ctx.moveTo(0, ty); ctx.lineTo(W, ty); }
    ctx.stroke();
    // Subtle perforation grain
    ctx.fillStyle = 'rgba(0,0,0,0.10)';
    for (let i = 0; i < 26000; i++) ctx.fillRect(rand() * W, rand() * H, 1.4, 1.4);
    ctx.fillStyle = 'rgba(255,255,255,0.035)';
    for (let i = 0; i < 9000; i++) ctx.fillRect(rand() * W, rand() * H, 1.6, 1.6);

    // Centre-ice spotlight wash
    const g = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, H * 0.55);
    g.addColorStop(0, 'rgba(120,180,255,0.10)');
    g.addColorStop(1, 'rgba(0,0,0,0.18)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);

    const RED   = '#d8232f';
    const WHITE = 'rgba(232,238,248,0.82)';

    // Clip all markings to the rounded rink outline
    ctx.save();
    roundRectPath(ctx, 0, 0, W, H, CORNER_R * PPU);
    ctx.clip();

    // Centre line (red with white pin-stripes)
    ctx.fillStyle = WHITE;
    ctx.fillRect(0, Y(0) - 0.22 * PPU, W, 0.44 * PPU);
    ctx.fillStyle = RED;
    ctx.fillRect(0, Y(0) - 0.16 * PPU, W, 0.32 * PPU);

    // Goal lines
    ctx.fillStyle = RED;
    [GOAL_LINE_Y, -GOAL_LINE_Y].forEach(gy => ctx.fillRect(0, Y(gy) - 0.05 * PPU, W, 0.1 * PPU));

    // Centre circle + logo disc
    ctx.beginPath();
    ctx.arc(X(0), Y(0), 2.5 * PPU, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(10,30,60,0.35)';
    ctx.fill();
    ctx.lineWidth = 0.12 * PPU;
    ctx.strokeStyle = WHITE;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(X(0), Y(0), 1.15 * PPU, 0, Math.PI * 2);
    ctx.lineWidth = 0.05 * PPU;
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.stroke();
    ctx.save();
    ctx.translate(X(0), Y(0));
    ctx.fillStyle = 'rgba(255,255,255,0.16)';
    ctx.font = `900 ${0.62 * PPU}px "Arial Black", Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('HOCKEY', 0, -0.5 * PPU);
    ctx.fillText('LÍNEA', 0, 0.55 * PPU);
    ctx.restore();
    ctx.beginPath();
    ctx.arc(X(0), Y(0), 0.22 * PPU, 0, Math.PI * 2);
    ctx.fillStyle = WHITE;
    ctx.fill();

    // Face-off circles + dots
    [[-5, 8], [5, 8], [-5, -8], [5, -8]].forEach(([fx, fy]) => {
        ctx.beginPath();
        ctx.arc(X(fx), Y(fy), 2.0 * PPU, 0, Math.PI * 2);
        ctx.lineWidth = 0.07 * PPU;
        ctx.strokeStyle = RED;
        ctx.stroke();
        // hash marks
        ctx.fillStyle = RED;
        [-1, 1].forEach(sx => [-1, 1].forEach(sy => {
            ctx.fillRect(X(fx + sx * 0.35) - 0.035 * PPU, Y(fy + sy * 2.0) - (sy > 0 ? 0.35 * PPU : 0), 0.07 * PPU, 0.35 * PPU);
        }));
        ctx.beginPath();
        ctx.arc(X(fx), Y(fy), 0.24 * PPU, 0, Math.PI * 2);
        ctx.fill();
    });

    // Goal creases (light blue fill, red outline) — semicircle toward centre ice
    [[GOAL_LINE_Y, 1], [-GOAL_LINE_Y, -1]].forEach(([gy, dir]) => {
        ctx.beginPath();
        // dir=+1 → top goal, crease opens toward -y (canvas +y)
        if (dir > 0) ctx.arc(X(0), Y(gy), CREASE_R * PPU, 0, Math.PI, false);
        else         ctx.arc(X(0), Y(gy), CREASE_R * PPU, Math.PI, Math.PI * 2, false);
        ctx.closePath();
        ctx.fillStyle = 'rgba(110,185,240,0.55)';
        ctx.fill();
        ctx.lineWidth = 0.07 * PPU;
        ctx.strokeStyle = RED;
        ctx.stroke();
        // goal frame footprint shadow inside net
        ctx.fillStyle = 'rgba(255,255,255,0.10)';
        const y0 = dir > 0 ? Y(gy + 1.2) : Y(gy);
        ctx.fillRect(X(-GOAL_W / 2), y0, GOAL_W * PPU, 1.2 * PPU);
    });

    // Scuff wear near the boards
    ctx.restore();
    ctx.save();
    roundRectPath(ctx, 0, 0, W, H, CORNER_R * PPU);
    ctx.lineWidth = 0.9 * PPU;
    ctx.strokeStyle = 'rgba(0,0,0,0.28)';
    ctx.filter = 'blur(12px)';
    ctx.stroke();
    ctx.restore();

    return toTexture(c, { aniso });
}

// ─── Jersey (wraps the torso lathe; u=0/1 is the back, u=0.5 the front) ──────
export function makeJerseyTexture(color, label) {
    const W = 512, H = 256;
    const [c, ctx] = makeCanvas(W, H);
    const base = new THREE.Color(color);
    const dark = base.clone().multiplyScalar(0.45);
    const light = luminance(color) > 0.55;
    const trim = light ? '#1b2333' : '#ffffff';

    ctx.fillStyle = `#${base.getHexString()}`;
    ctx.fillRect(0, 0, W, H);
    // fabric weave
    const rand = rng(label.length * 31 + 3);
    ctx.fillStyle = 'rgba(0,0,0,0.05)';
    for (let i = 0; i < 2200; i++) ctx.fillRect(rand() * W, rand() * H, 2, 1);
    // shoulder yoke
    ctx.fillStyle = `#${dark.getHexString()}`;
    ctx.fillRect(0, 0, W, 34);
    // hem stripes
    ctx.fillStyle = trim;
    ctx.fillRect(0, H - 52, W, 12);
    ctx.fillRect(0, H - 32, W, 6);
    ctx.fillStyle = `#${dark.getHexString()}`;
    ctx.fillRect(0, H - 40, W, 8);

    const drawNumber = (x, y, size, maxW) => {
        ctx.font = `900 ${size}px "Arial Black", Impact, Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const scale = Math.min(1, maxW / Math.max(1, ctx.measureText(label).width));
        ctx.save();
        ctx.translate(x, y);
        ctx.scale(scale, 1);
        ctx.lineJoin = 'round';
        ctx.lineWidth = size * 0.14;
        ctx.strokeStyle = light ? '#ffffff' : `#${dark.getHexString()}`;
        ctx.strokeText(label, 0, 0);
        ctx.fillStyle = trim;
        ctx.fillText(label, 0, 0);
        ctx.restore();
    };
    // Back number straddles the u seam (drawn at both edges)
    drawNumber(0, 118, 96, 210);
    drawNumber(W, 118, 96, 210);
    // Front: crest disc + small number
    ctx.beginPath();
    ctx.arc(W / 2, 92, 30, 0, Math.PI * 2);
    ctx.fillStyle = trim;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(W / 2, 92, 24, 0, Math.PI * 2);
    ctx.fillStyle = `#${dark.getHexString()}`;
    ctx.fill();
    drawNumber(W / 2, 150, 44, 110);

    return toTexture(c);
}

export function makeSleeveTexture(color) {
    const [c, ctx] = makeCanvas(64, 128);
    const base = new THREE.Color(color);
    const light = luminance(color) > 0.55;
    ctx.fillStyle = `#${base.getHexString()}`;
    ctx.fillRect(0, 0, 64, 128);
    ctx.fillStyle = light ? '#1b2333' : '#ffffff';
    ctx.fillRect(0, 40, 64, 10);
    ctx.fillStyle = `#${base.clone().multiplyScalar(0.45).getHexString()}`;
    ctx.fillRect(0, 54, 64, 6);
    return toTexture(c);
}

// ─── Name badge floating above the player ─────────────────────────────────────
export function makeBadgeTexture(label, color) {
    const W = 256, H = 96;
    const [c, ctx] = makeCanvas(W, H);
    roundRectPath(ctx, 6, 14, W - 12, H - 28, 30);
    ctx.fillStyle = 'rgba(8,18,34,0.86)';
    ctx.fill();
    ctx.lineWidth = 4;
    ctx.strokeStyle = color;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(40, H / 2, 13, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = '800 40px "Segoe UI", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, W / 2 + 16, H / 2 + 2, W - 90);
    return toTexture(c);
}

// ─── Radial gradients (contact shadows, glows) ────────────────────────────────
export function makeRadialTexture(stops) {
    const S = 128;
    const [c, ctx] = makeCanvas(S, S);
    const g = ctx.createRadialGradient(S / 2, S / 2, 0, S / 2, S / 2, S / 2);
    stops.forEach(([o, col]) => g.addColorStop(o, col));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, S, S);
    return toTexture(c, { srgb: false });
}

// ─── Board advertising strip ──────────────────────────────────────────────────
export function makeAdTexture(seed = 1) {
    const W = 2048, H = 64;
    const [c, ctx] = makeCanvas(W, H);
    const panels = [
        ['#0c2340', '#ffffff', 'HOCKEY LÍNEA'],
        ['#ffffff', '#0c2340', 'TÁCTICA'],
        ['#c8102e', '#ffffff', 'INLINE'],
        ['#ffffff', '#c8102e', 'JUEGO LIMPIO'],
        ['#111827', '#f6c900', 'PLAY HARD'],
        ['#f6c900', '#111827', 'CANTERA'],
    ];
    const n = 8;
    const pw = W / n;
    for (let i = 0; i < n; i++) {
        const [bg, fg, txt] = panels[(i + seed) % panels.length];
        ctx.fillStyle = bg;
        ctx.fillRect(i * pw, 0, pw, H);
        ctx.fillStyle = fg;
        ctx.font = '900 34px "Arial Black", Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(txt, i * pw + pw / 2, H / 2 + 2, pw - 24);
    }
    return toTexture(c, { repeat: [1, 1] });
}

// ─── Net mesh alpha (diamond netting) ─────────────────────────────────────────
export function makeNetTexture() {
    const S = 64;
    const [c, ctx] = makeCanvas(S, S);
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, S, S);
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(0, 0); ctx.lineTo(S, S);
    ctx.moveTo(S, 0); ctx.lineTo(0, S);
    ctx.stroke();
    const tex = toTexture(c, { srgb: false, repeat: [1, 1] });
    return tex;
}

// ─── Centre-hung scoreboard face ──────────────────────────────────────────────
export function makeScoreboardTexture(title, subtitle) {
    const W = 512, H = 256;
    const [c, ctx] = makeCanvas(W, H);
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, '#071a33');
    g.addColorStop(1, '#020812');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    // LED dot matrix
    ctx.fillStyle = 'rgba(255,255,255,0.035)';
    for (let y = 2; y < H; y += 5) for (let x = 2; x < W; x += 5) ctx.fillRect(x, y, 2, 2);
    ctx.fillStyle = '#c8102e';
    ctx.fillRect(0, 0, W, 10);
    ctx.fillRect(0, H - 10, W, 10);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = '#7fc4ff';
    ctx.font = '700 28px "Segoe UI", Arial, sans-serif';
    ctx.fillText(subtitle, W / 2, 62, W - 40);
    ctx.fillStyle = '#ffffff';
    ctx.shadowColor = '#4aa8ff';
    ctx.shadowBlur = 18;
    ctx.font = '900 54px "Arial Black", Arial, sans-serif';
    ctx.fillText(title, W / 2, 142, W - 40);
    return toTexture(c);
}

// ─── Crowd seat-row noise ─────────────────────────────────────────────────────
export function makeSeatTexture() {
    const [c, ctx] = makeCanvas(256, 64);
    const rand = rng(11);
    ctx.fillStyle = '#16233a';
    ctx.fillRect(0, 0, 256, 64);
    for (let x = 0; x < 256; x += 8) {
        ctx.fillStyle = rand() > 0.5 ? '#1d3150' : '#132038';
        ctx.fillRect(x + 1, 6, 6, 40);
    }
    return toTexture(c, { repeat: [12, 1] });
}
